import React, { useState } from "react";
import {
  CheckCircle2,
  Link,
  Plus,
  Image as ImageIcon,
  Upload,
  Percent,
  Square,
  CheckSquare,
  Edit2,
  X,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AutocompleteInput from "@/components/common/AutocompleteInput";

// Format number
const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

// Image Thumbnail Component
const ImageThumbnail = ({ src, alt, className }) => {
  const [hasError, setHasError] = React.useState(false);
  
  if (!src || hasError) {
    return (
      <div className={`bg-slate-100 flex items-center justify-center ${className}`}>
        <Square className="w-4 h-4 text-slate-300" />
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt} 
      className={`object-contain ${className}`}
      onError={() => setHasError(true)}
    />
  );
};

/**
 * FactoryParsedItemsModal - Modal for reviewing and editing parsed factory cost items
 */
const FactoryParsedItemsModal = ({
  isOpen,
  onClose,
  factoryParsedItems,
  factoryParsedFilename,
  factoryProjectName,
  factoryInvoiceNo,
  factoryAllImages,
  materials,
  availableFactories,
  allProjects,
  allTypeNames,
  imageSuggestionsOpen,
  imageSuggestions,
  factoryDimensionUnit,
  setFactoryDimensionUnit,
  factoryBulkMaterial,
  setFactoryBulkMaterial,
  factoryBulkColor,
  setFactoryBulkColor,
  factoryBulkFactory,
  setFactoryBulkFactory,
  factoryAdjustPercent,
  setFactoryAdjustPercent,
  factoryAdjustApplied,
  setFactoryAdjustApplied,
  // Functions
  handleProjectNameChange,
  autoMatchProjectItems,
  applyFactoryBulkMaterial,
  applyFactoryBulkColor,
  applyFactoryBulkFactory,
  applyFactoryPercentAdjust,
  updateFactoryParsedItem,
  loadImageSuggestions,
  setImageSuggestionsOpen,
  saveFactoryParsedItems,
  toast
}) => {
  const itemsWithoutMaterial = factoryParsedItems.filter(i => !i.material).length;
  const isValid = itemsWithoutMaterial === 0;

  // Bulk edit state for multi-selection
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkOpeningDesign, setBulkOpeningDesign] = useState("");
  const [bulkMaterialEdit, setBulkMaterialEdit] = useState("");
  const [bulkColorEdit, setBulkColorEdit] = useState("");
  const [bulkGlass, setBulkGlass] = useState("");
  const [bulkHardware, setBulkHardware] = useState("");

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Toggle all items selection
  const toggleAllSelection = () => {
    if (selectedItems.size === factoryParsedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(factoryParsedItems.map((item) => item.id)));
    }
  };

  // Apply bulk changes to selected items
  const applyBulkEdits = () => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item");
      return;
    }
    
    let changes = 0;
    
    selectedItems.forEach((itemId) => {
      if (bulkOpeningDesign.trim()) {
        updateFactoryParsedItem(itemId, 'opening_design', bulkOpeningDesign);
        changes++;
      }
      if (bulkMaterialEdit.trim()) {
        updateFactoryParsedItem(itemId, 'material', bulkMaterialEdit);
        changes++;
      }
      if (bulkColorEdit.trim()) {
        updateFactoryParsedItem(itemId, 'color', bulkColorEdit);
        changes++;
      }
      if (bulkGlass.trim()) {
        updateFactoryParsedItem(itemId, 'glass_spec', bulkGlass);
        changes++;
      }
      if (bulkHardware.trim()) {
        updateFactoryParsedItem(itemId, 'lock_type', bulkHardware);
        changes++;
      }
    });
    
    if (changes === 0) {
      toast.warning("Ingresa al menos un valor para aplicar");
      return;
    }
    
    toast.success(`Cambios aplicados a ${selectedItems.size} items`);
    
    // Clear bulk inputs
    setBulkOpeningDesign("");
    setBulkMaterialEdit("");
    setBulkColorEdit("");
    setBulkGlass("");
    setBulkHardware("");
  };

  // Clear selection and inputs
  const clearBulkSelection = () => {
    setSelectedItems(new Set());
    setBulkOpeningDesign("");
    setBulkMaterialEdit("");
    setBulkColorEdit("");
    setBulkGlass("");
    setBulkHardware("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Revisar Costos de Fábrica - {factoryParsedFilename}
          </DialogTitle>
          <DialogDescription>
            Asigna el material y nombre a cada elemento antes de guardar
          </DialogDescription>
          {/* Project Info Banner */}
          {(factoryProjectName || factoryInvoiceNo) && (
            <div className="flex items-center gap-4 mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md" data-testid="factory-project-banner">
              {factoryProjectName && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-700">Proyecto/Cliente:</span>
                  <span className="text-sm text-blue-900 font-semibold" data-testid="factory-project-name">{factoryProjectName}</span>
                </div>
              )}
              {factoryInvoiceNo && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-700">Factura:</span>
                  <span className="text-sm text-blue-900" data-testid="factory-invoice-no">{factoryInvoiceNo}</span>
                </div>
              )}
            </div>
          )}
        </DialogHeader>
        
        {/* Bulk Assignment Section */}
        <div className="flex flex-wrap gap-4 p-3 bg-slate-50 rounded-sm border">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-slate-700 whitespace-nowrap">Asignar a todos:</Label>
          </div>
          <div className="flex items-center gap-2">
            <Select value={factoryBulkMaterial} onValueChange={setFactoryBulkMaterial}>
              <SelectTrigger className="w-[130px] h-8">
                <SelectValue placeholder="Material" />
              </SelectTrigger>
              <SelectContent>
                {materials.map((mat) => (
                  <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="secondary" onClick={applyFactoryBulkMaterial} disabled={!factoryBulkMaterial}>
              Aplicar Material
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={factoryBulkColor} onValueChange={setFactoryBulkColor}>
              <SelectTrigger className="w-[130px] h-8">
                <SelectValue placeholder="Color" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="White">White</SelectItem>
                <SelectItem value="Black">Black</SelectItem>
                <SelectItem value="Grey">Grey</SelectItem>
                <SelectItem value="Powder Coated">Powder Coated</SelectItem>
                <SelectItem value="Anodized">Anodized</SelectItem>
                <SelectItem value="Natural">Natural</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="secondary" onClick={applyFactoryBulkColor} disabled={!factoryBulkColor}>
              Aplicar Color
            </Button>
          </div>
          {/* Factory Name Assignment */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Input
                className="w-[150px] h-8"
                placeholder="Nombre Fábrica"
                value={factoryBulkFactory}
                onChange={(e) => setFactoryBulkFactory(e.target.value)}
                list="factory-suggestions"
              />
              <datalist id="factory-suggestions">
                {availableFactories.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </div>
            <Button size="sm" variant="secondary" onClick={applyFactoryBulkFactory} disabled={!factoryBulkFactory}>
              Aplicar Fábrica
            </Button>
          </div>
          {/* Project Name Assignment with Auto-Match - REQUIRED */}
          <div className="flex items-center gap-2 border-l pl-4">
            <div className="relative">
              <Input
                className={`w-[180px] h-8 ${!factoryProjectName?.trim() ? 'border-red-400 bg-red-50' : 'border-blue-300'} focus:border-blue-500`}
                placeholder="Proyecto (obligatorio) *"
                value={factoryProjectName}
                onChange={(e) => handleProjectNameChange(e.target.value)}
                list="project-suggestions"
                data-testid="factory-project-input"
              />
              <datalist id="project-suggestions">
                {allProjects.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-400 text-blue-700 hover:bg-blue-50"
              onClick={() => autoMatchProjectItems(factoryProjectName)}
              disabled={!factoryProjectName}
              title="Buscar elementos coincidentes del proyecto"
            >
              <Link className="w-4 h-4 mr-1" />
              Match
            </Button>
          </div>
          {/* Percentage Adjustment */}
          <div className="flex items-center gap-2 border-l pl-4">
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.1"
                className={`w-[80px] h-8 text-right font-mono ${factoryAdjustApplied ? 'bg-green-50 border-green-400' : ''}`}
                placeholder="% Ajuste"
                value={factoryAdjustPercent}
                onChange={(e) => {
                  setFactoryAdjustPercent(e.target.value);
                  setFactoryAdjustApplied(false);
                }}
                data-testid="factory-adjust-percent"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
            <Button 
              size="sm" 
              variant={factoryAdjustApplied ? "default" : "secondary"}
              className={factoryAdjustApplied ? "bg-green-600 hover:bg-green-700" : ""}
              onClick={applyFactoryPercentAdjust}
              disabled={!factoryAdjustPercent || factoryAdjustApplied}
              title="Aplicar porcentaje a todos los valores $/m²"
            >
              <Percent className="w-4 h-4 mr-1" />
              {factoryAdjustApplied ? 'Aplicado' : 'Aplicar'}
            </Button>
          </div>
          <div className="ml-auto text-sm text-slate-500">
            {factoryAllImages.length} imágenes extraídas del documento
          </div>
        </div>
        
        {/* Multi-Selection Bulk Edit Section */}
        {factoryParsedItems.length > 0 && (
          <div className="p-3 bg-blue-50 rounded-sm border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Edición Masiva (Selección Múltiple)</span>
                {selectedItems.size > 0 ? (
                  <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                    {selectedItems.size} de {factoryParsedItems.length} seleccionados
                  </span>
                ) : (
                  <span className="text-xs text-blue-500">(Usa los checkbox en la tabla)</span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleAllSelection}
                className="h-7 text-xs text-blue-700 hover:bg-blue-100"
                data-testid="factory-bulk-select-all"
              >
                {selectedItems.size === factoryParsedItems.length ? (
                  <>
                    <Square className="w-3 h-3 mr-1" />
                    Deseleccionar Todo
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-3 h-3 mr-1" />
                    Seleccionar Todo
                  </>
                )}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
              <div>
                <Label className="text-xs text-blue-600">Diseño Apertura</Label>
                <AutocompleteInput
                  value={bulkOpeningDesign}
                  onChange={(e) => setBulkOpeningDesign(typeof e === 'string' ? e : e.target.value)}
                  suggestions={allTypeNames}
                  className="h-8 text-xs"
                  placeholder="Ej: VENTANA CORREDIZA"
                  maxSuggestions={15}
                  minCharsToShow={1}
                  data-testid="factory-bulk-opening-design"
                />
              </div>
              <div>
                <Label className="text-xs text-blue-600">Material</Label>
                <Select value={bulkMaterialEdit} onValueChange={setBulkMaterialEdit}>
                  <SelectTrigger className="h-8 text-xs" data-testid="factory-bulk-material-edit">
                    <SelectValue placeholder="Material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materials.map((mat) => (
                      <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-blue-600">Color</Label>
                <Select value={bulkColorEdit} onValueChange={setBulkColorEdit}>
                  <SelectTrigger className="h-8 text-xs" data-testid="factory-bulk-color-edit">
                    <SelectValue placeholder="Color" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="White">White</SelectItem>
                    <SelectItem value="Black">Black</SelectItem>
                    <SelectItem value="Grey">Grey</SelectItem>
                    <SelectItem value="Powder Coated">Powder Coated</SelectItem>
                    <SelectItem value="Anodized">Anodized</SelectItem>
                    <SelectItem value="Natural">Natural</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-blue-600">Vidrio</Label>
                <Input
                  value={bulkGlass}
                  onChange={(e) => setBulkGlass(e.target.value)}
                  className="h-8 text-xs"
                  placeholder="Ej: 6mm templado"
                  data-testid="factory-bulk-glass"
                />
              </div>
              <div>
                <Label className="text-xs text-blue-600">Cerradura</Label>
                <Input
                  value={bulkHardware}
                  onChange={(e) => setBulkHardware(e.target.value)}
                  className="h-8 text-xs"
                  placeholder="Ej: Cremona"
                  data-testid="factory-bulk-hardware"
                />
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  onClick={applyBulkEdits}
                  disabled={selectedItems.size === 0}
                  className="h-8 text-xs flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="factory-bulk-apply"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Aplicar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearBulkSelection}
                  className="h-8 text-xs border-blue-300 text-blue-600 hover:bg-blue-100"
                  title="Limpiar selección y campos"
                  data-testid="factory-bulk-clear"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <ScrollArea className="h-[48vh] border rounded-sm">
            <div className="min-w-[1300px]">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-[40px] p-1">
                      <button
                        onClick={toggleAllSelection}
                        className="flex items-center justify-center w-6 h-6 hover:bg-slate-100 rounded"
                        title={selectedItems.size === factoryParsedItems.length ? "Deseleccionar todo" : "Seleccionar todo"}
                      >
                        {selectedItems.size === factoryParsedItems.length && factoryParsedItems.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-[70px]">Imagen</TableHead>
                    <TableHead className="w-[80px]">Código</TableHead>
                    <TableHead className="w-[150px]">Diseño Apertura</TableHead>
                    <TableHead className="text-right w-[80px]">
                      <div className="flex items-center justify-end gap-1">
                        <span>Ancho</span>
                        <Select value={factoryDimensionUnit} onValueChange={setFactoryDimensionUnit}>
                          <SelectTrigger className="h-5 w-[50px] text-[10px] px-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mm">mm</SelectItem>
                            <SelectItem value="m">m</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    <TableHead className="text-right w-[80px]">
                      <div className="flex items-center justify-end gap-1">
                        <span>Alto</span>
                        <span className="text-[10px] text-slate-400">({factoryDimensionUnit})</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-right w-[80px]">Costo USD</TableHead>
                    <TableHead className="text-right w-[80px]">$/m² USD</TableHead>
                    <TableHead className="w-[100px]">Material</TableHead>
                    <TableHead className="w-[100px]">Fábrica</TableHead>
                    <TableHead className="w-[100px]">Color</TableHead>
                    <TableHead className="w-[100px]">Vidrio</TableHead>
                    <TableHead className="w-[120px]">Cerradura</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {factoryParsedItems.map((item) => (
                    <FactoryParsedItemRow
                      key={item.id}
                      item={item}
                      isSelected={selectedItems.has(item.id)}
                      toggleItemSelection={toggleItemSelection}
                      factoryAllImages={factoryAllImages}
                      materials={materials}
                      availableFactories={availableFactories}
                      allTypeNames={allTypeNames}
                      imageSuggestionsOpen={imageSuggestionsOpen}
                      imageSuggestions={imageSuggestions}
                      factoryDimensionUnit={factoryDimensionUnit}
                      updateFactoryParsedItem={updateFactoryParsedItem}
                      loadImageSuggestions={loadImageSuggestions}
                      setImageSuggestionsOpen={setImageSuggestionsOpen}
                      toast={toast}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </ScrollArea>
        </div>
        
        {/* Footer - Sticky at bottom */}
        <div className="flex items-center justify-between pt-4 pb-2 border-t bg-white mt-auto sticky bottom-0">
          <div className={itemsWithoutMaterial > 0 ? "text-amber-600 font-medium text-sm" : "text-emerald-600 text-sm"}>
            Material asignado: {factoryParsedItems.filter(i => i.material).length} de {factoryParsedItems.length}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={saveFactoryParsedItems}
              disabled={!isValid}
              data-testid="save-factory-items-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Guardar {factoryParsedItems.length} Costos
            </Button>
          </div>
        </div>
        
      </DialogContent>
    </Dialog>
  );
};

/**
 * FactoryParsedItemRow - Individual row for factory parsed items table
 */
const FactoryParsedItemRow = ({
  item,
  isSelected,
  toggleItemSelection,
  factoryAllImages,
  materials,
  availableFactories,
  allTypeNames,
  imageSuggestionsOpen,
  imageSuggestions,
  factoryDimensionUnit,
  updateFactoryParsedItem,
  loadImageSuggestions,
  setImageSuggestionsOpen,
  toast
}) => {
  const handleImagePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          const reader = new FileReader();
          reader.onloadend = () => {
            updateFactoryParsedItem(item.id, 'image_data', reader.result);
            toast.success('Imagen pegada correctamente');
          };
          reader.readAsDataURL(blob);
          break;
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-blue-500', 'bg-blue-100');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100');
    const files = e.dataTransfer.files;
    if (files && files[0] && files[0].type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateFactoryParsedItem(item.id, 'image_data', reader.result);
        toast.success('Imagen agregada correctamente');
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleFileClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          updateFactoryParsedItem(item.id, 'image_data', reader.result);
          toast.success('Imagen cargada correctamente');
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleWidthChange = (e) => {
    let val = parseFloat(e.target.value) || 0;
    if (factoryDimensionUnit === "mm") {
      val = val / 1000;
    }
    const area = val * (item.height_m || 0);
    const costM2 = area > 0 && item.cost_usd_set ? item.cost_usd_set / area : null;
    updateFactoryParsedItem(item.id, 'width_m', val);
    updateFactoryParsedItem(item.id, 'area_m2', area);
    if (costM2) updateFactoryParsedItem(item.id, 'cost_usd_m2', costM2);
  };

  const handleHeightChange = (e) => {
    let val = parseFloat(e.target.value) || 0;
    if (factoryDimensionUnit === "mm") {
      val = val / 1000;
    }
    const area = (item.width_m || 0) * val;
    const costM2 = area > 0 && item.cost_usd_set ? item.cost_usd_set / area : null;
    updateFactoryParsedItem(item.id, 'height_m', val);
    updateFactoryParsedItem(item.id, 'area_m2', area);
    if (costM2) updateFactoryParsedItem(item.id, 'cost_usd_m2', costM2);
  };

  const handleCostChange = (e) => {
    const val = parseFloat(e.target.value) || 0;
    const area = (item.width_m || 0) * (item.height_m || 0);
    const costM2 = area > 0 ? val / area : null;
    updateFactoryParsedItem(item.id, 'cost_usd_set', val);
    if (costM2) updateFactoryParsedItem(item.id, 'cost_usd_m2', costM2);
  };

  return (
    <TableRow className={`${!item.material ? 'bg-amber-50' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
      {/* Checkbox Cell */}
      <TableCell className="p-1">
        <button
          onClick={() => toggleItemSelection(item.id)}
          className="flex items-center justify-center w-6 h-6 hover:bg-slate-100 rounded"
          data-testid={`factory-item-checkbox-${item.id}`}
        >
          {isSelected ? (
            <CheckSquare className="w-4 h-4 text-blue-600" />
          ) : (
            <Square className="w-4 h-4 text-slate-400" />
          )}
        </button>
      </TableCell>
      {/* Image Cell */}
      <TableCell className="p-2">
        <Popover>
          <PopoverTrigger asChild>
            <button className="relative group cursor-pointer">
              <ImageThumbnail
                src={item.image_data}
                alt={item.item_code}
                className="w-12 h-12 rounded border border-slate-200 group-hover:border-blue-400"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded flex items-center justify-center transition-opacity">
                <ImageIcon className="w-4 h-4 text-white" />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[500px] p-2" side="right">
            <div className="space-y-2">
              <div className="flex items-center justify-between border-b pb-2">
                <p className="text-sm font-medium">Seleccionar Imagen para {item.item_code}</p>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => loadImageSuggestions(item.id, item.opening_design || item.custom_name || item.item_code)}
                    className="h-7 text-xs"
                  >
                    Buscar Sugerencias
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => updateFactoryParsedItem(item.id, 'image_data', null)}
                    className="text-red-600 h-7"
                  >
                    Sin imagen
                  </Button>
                </div>
              </div>
              
              {/* Paste/Drop image area */}
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50 hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                tabIndex={0}
                onPaste={handleImagePaste}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleFileClick}
              >
                <div className="text-center">
                  <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                  <p className="text-xs text-slate-600 font-medium">Pegar (Ctrl+V), arrastrar o clic para subir</p>
                  <p className="text-[10px] text-slate-400 mt-1">Copia la imagen desde Word, Excel, PDF y pégala aquí</p>
                </div>
              </div>
              
              {/* Image suggestions from database */}
              {imageSuggestionsOpen === item.id && imageSuggestions.length > 0 && (
                <div className="border-b pb-2">
                  <p className="text-xs font-medium text-blue-700 mb-2">
                    Sugerencias basadas en &quot;{item.opening_design || item.item_code}&quot; ({imageSuggestions.length} encontradas)
                  </p>
                  <ScrollArea className="h-[150px]">
                    <div className="grid grid-cols-4 gap-2">
                      {imageSuggestions
                        .filter(s => s.image_data)
                        .map((suggestion) => (
                          <button
                            key={`img-${suggestion.id || suggestion.name}-${suggestion.match_score}`}
                            onClick={() => {
                              updateFactoryParsedItem(item.id, 'image_data', suggestion.image_data);
                              setImageSuggestionsOpen(null);
                            }}
                            className={`border rounded p-1 hover:border-blue-400 transition-colors relative ${
                              suggestion.match_score >= 70 ? 'border-green-400 bg-green-50' :
                              suggestion.match_score >= 40 ? 'border-yellow-400 bg-yellow-50' :
                              'border-slate-200'
                            }`}
                            title={`${suggestion.name} - ${suggestion.match_score}% coincidencia`}
                          >
                            <img 
                              src={suggestion.image_data} 
                              alt={suggestion.name}
                              className="w-full h-14 object-contain"
                            />
                            <div className={`absolute top-0 right-0 text-[9px] px-1 rounded-bl ${
                              suggestion.match_score >= 70 ? 'bg-green-500 text-white' :
                              suggestion.match_score >= 40 ? 'bg-yellow-500 text-white' :
                              'bg-slate-400 text-white'
                            }`}>
                              {suggestion.match_score}%
                            </div>
                            <p className="text-[9px] text-slate-600 truncate mt-1" title={suggestion.name}>
                              {suggestion.name}
                            </p>
                          </button>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              
              {/* Images from current document */}
              {factoryAllImages.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-2">
                    Imágenes del documento actual ({factoryAllImages.length})
                  </p>
                  <ScrollArea className="h-[150px]">
                    <div className="grid grid-cols-4 gap-2">
                      {factoryAllImages.map((imgData, idx) => (
                        <button
                          key={`gallery-${idx}-${imgData.slice(-20)}`}
                          onClick={() => updateFactoryParsedItem(item.id, 'image_data', imgData)}
                          className={`border rounded p-1 hover:border-blue-400 transition-colors ${
                            item.image_data === imgData ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'
                          }`}
                        >
                          <img 
                            src={imgData} 
                            alt={`Imagen ${idx + 1}`}
                            className="w-full h-16 object-contain"
                          />
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              
              {factoryAllImages.length === 0 && imageSuggestions.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  No hay imágenes disponibles. Haz clic en &quot;Buscar Sugerencias&quot; para encontrar imágenes similares.
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </TableCell>
      
      {/* Item Code Cell */}
      <TableCell className="p-1">
        <Input
          value={item.item_code || ""}
          onChange={(e) => updateFactoryParsedItem(item.id, 'item_code', e.target.value)}
          className="h-8 text-xs font-mono w-[70px]"
          placeholder="Código"
        />
      </TableCell>
      
      {/* Opening Design Cell */}
      <TableCell className="p-1">
        <AutocompleteInput
          value={item.opening_design || ""}
          onChange={(e) => updateFactoryParsedItem(item.id, 'opening_design', e.target.value)}
          suggestions={allTypeNames}
          className="h-8 text-xs w-[140px]"
          placeholder="Diseño"
          maxSuggestions={15}
          minCharsToShow={1}
        />
      </TableCell>
      
      {/* Width Cell */}
      <TableCell className="p-1">
        <Input
          type="number"
          step={factoryDimensionUnit === "mm" ? "1" : "0.001"}
          value={factoryDimensionUnit === "mm" ? Math.round((item.width_m || 0) * 1000) : (item.width_m || "")}
          onChange={handleWidthChange}
          className="h-8 text-xs font-mono w-[80px] text-right"
        />
      </TableCell>
      
      {/* Height Cell */}
      <TableCell className="p-1">
        <Input
          type="number"
          step={factoryDimensionUnit === "mm" ? "1" : "0.001"}
          value={factoryDimensionUnit === "mm" ? Math.round((item.height_m || 0) * 1000) : (item.height_m || "")}
          onChange={handleHeightChange}
          className="h-8 text-xs font-mono w-[80px] text-right"
        />
      </TableCell>
      
      {/* Cost Cell */}
      <TableCell className="p-1">
        <Input
          type="number"
          step="0.01"
          value={item.cost_usd_set || ""}
          onChange={handleCostChange}
          className="h-8 text-xs font-mono w-[90px] text-right"
        />
      </TableCell>
      
      {/* Cost per m² Cell */}
      <TableCell className="text-right font-mono text-sm text-emerald-600 p-2">
        {item.cost_usd_m2 ? `$${formatNumber(item.cost_usd_m2)}` : "—"}
      </TableCell>
      
      {/* Material Cell */}
      <TableCell className="p-1">
        <Select
          value={item.material || ""}
          onValueChange={(value) => updateFactoryParsedItem(item.id, 'material', value)}
        >
          <SelectTrigger className={`w-full h-8 ${!item.material ? "border-amber-400" : ""}`}>
            <SelectValue placeholder="Material" />
          </SelectTrigger>
          <SelectContent>
            {materials.map((mat) => (
              <SelectItem key={mat} value={mat}>{mat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      
      {/* Factory Name Cell */}
      <TableCell className="p-1">
        <div className="relative">
          <Input
            value={item.factory_name || ""}
            onChange={(e) => updateFactoryParsedItem(item.id, 'factory_name', e.target.value)}
            className="h-8 text-xs w-[90px]"
            placeholder="Fábrica"
            list={`factory-list-${item.id}`}
          />
          <datalist id={`factory-list-${item.id}`}>
            {availableFactories.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </div>
      </TableCell>
      
      {/* Color Cell */}
      <TableCell className="p-1">
        <Select
          value={item.color || ""}
          onValueChange={(value) => updateFactoryParsedItem(item.id, 'color', value)}
        >
          <SelectTrigger className="w-full h-8">
            <SelectValue placeholder="Color" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="White">White</SelectItem>
            <SelectItem value="Black">Black</SelectItem>
            <SelectItem value="Grey">Grey</SelectItem>
            <SelectItem value="Powder Coated">Powder Coated</SelectItem>
            <SelectItem value="Anodized">Anodized</SelectItem>
            <SelectItem value="Natural">Natural</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      
      {/* Glass Spec Cell */}
      <TableCell className="p-1">
        <Input
          value={item.glass_spec || ""}
          onChange={(e) => updateFactoryParsedItem(item.id, 'glass_spec', e.target.value)}
          className="h-8 text-xs w-[80px]"
          placeholder="Vidrio"
        />
      </TableCell>
      
      {/* Lock Type Cell */}
      <TableCell className="p-1">
        <Input
          value={item.lock_type || ""}
          onChange={(e) => updateFactoryParsedItem(item.id, 'lock_type', e.target.value)}
          className="h-8 text-xs w-[100px]"
          placeholder="Cerradura"
        />
      </TableCell>
    </TableRow>
  );
};

export { FactoryParsedItemsModal, FactoryParsedItemRow };
export default FactoryParsedItemsModal;
